import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Paper,
} from '@mui/material';
import {
  Timeline,
  InsertChartOutlined,
  Speed,
  ShowChart,
  BarChart,
} from '@mui/icons-material';

import PrimaryLayout from '../components/layout/PrimaryLayout';

const Dashboard: React.FC = () => {
  return (
    <PrimaryLayout>
      <Box sx={{ mx: 2, my: 1 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Insights
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visualize your infrastructure and business metrics with real-time monitoring and analytics.
        </Typography>
      </Box>

      <Container maxWidth="md" sx={{ py: 5 }}>
        <Grid container spacing={4}>
          {/* Pulse Card */}
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Timeline fontSize="large" color="primary" />
                  <Typography variant="h6">Pulse</Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Gain unified visibility into infrastructure health, system performance, and workflow execution with real-time Pulse dashboards.
                </Typography>

                <Box display="flex" gap={2} alignItems="center" mt={2}>
                  <Speed color="action" />
                  <Typography variant="body2">Real-time telemetry</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <ShowChart color="action" />
                  <Typography variant="body2">Workflow performance metrics</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <InsertChartOutlined color="action" />
                  <Typography variant="body2">System utilization trends</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <BarChart color="action" />
                  <Typography variant="body2">Task queue visualizations</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <Timeline color="action" />
                  <Typography variant="body2">Pipeline execution history</Typography>
                </Box>
              </Box>

              <Box mt={3}>
                <Button
                  component="a"
                  href={`${process.env.REACT_APP_GRAFANA_URL ?? '#'}/dashboards`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="primary"
                >
                  Open Pulse
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* BI Card */}
          <Grid item xs={12} md={6} sx={{ height: '100%' }}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <InsertChartOutlined fontSize="large" color="secondary" />
                  <Typography variant="h6">Business Intelligence</Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  Enable strategic decisions with curated BI dashboards offering insights into KPIs, business operations, and organizational performance.
                </Typography>

                <Box display="flex" gap={2} alignItems="center" mt={2}>
                  <ShowChart color="action" />
                  <Typography variant="body2">Business metrics and KPIs</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <InsertChartOutlined color="action" />
                  <Typography variant="body2">Custom dashboards and filters</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <BarChart color="action" />
                  <Typography variant="body2">Trend analysis and segmentation</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <Timeline color="action" />
                  <Typography variant="body2">Historical data exploration</Typography>
                </Box>

                <Box display="flex" gap={2} alignItems="center" mt={1}>
                  <Speed color="action" />
                  <Typography variant="body2">Funnel & cohort visualization</Typography>
                </Box>
              </Box>

              <Box mt={3}>
                <Button
                  component="a"
                  href={`${process.env.REACT_APP_BI_URL ?? '#'}/dashboard/list`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  color="secondary"
                >
                  Open BI
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </PrimaryLayout>
  );
};

export default Dashboard;
